import { Menu, LogOut } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentInstitute } from '@/hooks/useCurrentInstitute'
import { useRole } from '@/hooks/useRole'
import { useInstitutes } from '@/hooks/useInstitutes'
import { Badge, type BadgeVariant } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import Logo from '@/assets/logo.png'

interface TopNavProps {
  onToggleSidebar: () => void
}

const roleLabelMap: Record<string, string> = {
  superuser: 'Super User',
  manager: 'Manager',
  tutor: 'Tutor',
  student: 'Student',
  user: 'User',
}

const roleVariantMap: Record<string, BadgeVariant> = {
  superuser: 'danger',
  manager: 'info',
  tutor: 'info',
  student: 'success',
}

/** Top navigation bar with sidebar toggle, institute branding, role badge, and user menu. */
export function TopNav({ onToggleSidebar }: TopNavProps) {
  const { user, signOut } = useAuth()
  const { role, uid } = useRole()
  const { currentInstituteId, setCurrentInstituteId, canSelectGlobal } = useCurrentInstitute()
  const { data: institutes = [] } = useInstitutes(role, uid)

  const activeInstitute = institutes.find((institute) => institute.id === currentInstituteId) ?? null

  const handleSignOut = async () => {
    await signOut()
  }

  // Apply institute theme color as a CSS custom property on the header
  const headerStyle = activeInstitute?.themeColor
    ? ({ '--inst-color': activeInstitute.themeColor } as React.CSSProperties)
    : undefined

  return (
    <header
      className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4"
      style={headerStyle}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Show institute logo + name if branded, otherwise app logo */}
        {activeInstitute?.logoUrl ? (
          <div className="flex items-center gap-2">
            <img
              src={activeInstitute.logoUrl}
              alt={activeInstitute.name}
              className="h-8 w-8 rounded-full object-cover"
            />
            <span
              className="text-lg font-bold tracking-tight"
              style={activeInstitute.themeColor ? { color: activeInstitute.themeColor } : undefined}
            >
              {activeInstitute.name}
            </span>
          </div>
        ) : (
          <span className="text-lg font-bold text-primary-700 tracking-tight">
            <img src={Logo} alt="Taka LLT" className="h-8" />
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {((canSelectGlobal || institutes.length > 1) && user) && (
          <label className="hidden items-center gap-2 text-sm text-gray-600 md:flex">
            <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-gray-400">Institute</span>
            <select
              value={currentInstituteId ?? ''}
              onChange={(event) => setCurrentInstituteId(event.target.value || null)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm outline-none transition focus:border-primary-500"
            >
              {institutes.map((institute) => (
                <option key={institute.id} value={institute.id}>
                  {institute.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {role && (
          <Badge variant={roleVariantMap[role] ?? 'default'}>
            {roleLabelMap[role] ?? role}
          </Badge>
        )}

        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
          {user?.displayName?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? '?'}
        </div>

        <Button variant="ghost" size="sm" onClick={handleSignOut} aria-label="Sign out">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  )
}
